import { ProductPageData } from "../_types";

export const JSON_PRODUCT_MAJIK_SLINK: ProductPageData = {
  id: "majik-slink",
  timestamp: new Date("2026-08-07").toISOString(),
  lastUpdate: new Date("2026-08-07").toISOString(),
  version: "2.0.0",
  isEnabled: true,
  repository: {
    url: "https://github.com/Majikah/majik-slink",
    owner: "Majikah",
    repo: "majik-slink",
  },
  metadata: {
    productName: "Majik SLink",
    productSlug: "majik-slink",
    tagline: "Prove Ownership, Attribution, or Reference — Cryptographically",
    description:
      "Sign any public URL with your Majik Universal ID and publish a tamper-proof claim anyone can verify. Prove you own a domain, control a piece of content, or simply attest a connection to it — no central authority, no trust required.",
    seo: "URL ownership verification, cryptographic proof of ownership, signed URLs, social profile verification, post-quantum URL signing, identity linking, attribution, attestation, Majikah ecosystem",
    keywords: [
      "url-ownership",
      "verification",
      "proof-of-ownership",
      "attribution",
      "reference",
      "attestation",
      "signed-urls",
      "social-verification",
      "identity-linking",
      "majikah",
      "majik-universal-id",
      "post-quantum",
      "ed25519",
      "ml-dsa-87",
      "slink",
      "claim-types",
    ],
    category: "Security",
    subcategory: "URL Ownership & Identity Verification",
    photo: {
      thumbnail:
        "https://storage.majikah.solutions/public/Logo_MajikSLink_Majikah_Thumbnail_WEBP.webp",
      banner:
        "https://storage.majikah.solutions/public/MajikSLink_SmallTile.jpg",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikSLink_SocialCard.webp",
    },
    video: [],
  },
  hero: {
    title: "Majik SLink",
    subtitle: "Prove Ownership, Attribution, or Reference — Cryptographically",
    description:
      "Anyone can claim to run a YouTube channel or social profile. Majik SLink lets you prove it — by signing any public URL with your Majik Universal ID. Pick the claim that matches reality: full domain ownership, content you control, or a plain attestation. No central authority, no platform badges. Just math.",
    ctaButtons: [
      {
        label: "Sign a URL",
        url: "https://id.majikah.solutions/slink",
        variant: "primary",
      },
      {
        label: "Verify a SLink",
        url: "https://slink.majikah.solutions",
        variant: "primary",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-slink",
        variant: "secondary",
        external: true,
      },
      {
        label: "See Technical Details",
        url: "#technical-specs",
        variant: "tertiary",
      },
      {
        label: "Read Docs",
        url: "/products/majik-slink/docs",
        variant: "tertiary",
      },
    ],
    badges: [
      {
        icon: "link",
        label: "3 claim types",
        description: "Ownership, Attribution, or Reference — pick what's true.",
      },
      {
        icon: "shieldCheck",
        label: "Post-quantum signed",
        description: "Ed25519 + ML-DSA-87 hybrid, on every claim.",
      },
      {
        icon: "eye",
        label: "Public & trustless",
        description: "Anyone can verify — no account required.",
      },
      {
        icon: "tag",
        label: "Free",
        description: "Creating, verifying, and managing SLinks costs nothing.",
      },
    ],
  },
  overview: {
    title: "What is a Majik SLink?",
    paragraphs: [
      "A Majik SLink is a cryptographically signed claim binding your Majik Universal ID to a public URL — a YouTube channel, a social media account, a personal website, a repo, or any publicly accessible page.",
      'Every SLink is one of three claim types, chosen to match how you actually relate to the URL. Ownership and Attribution claims are backed by a challenge-response proof the system can independently check — a DNS TXT record for Ownership, or a short code embedded on the page for Attribution. Reference claims skip the proof step entirely: they\'re a signed attestation with no independent check possible, and are always displayed to others that way, never as "verified".',
      "Every SLink is permanently tied to your Majik Universal ID and shows up on your public profile. Visitors can see everything you've claimed and verify each one in a single click, with the claim type and proof strength clearly labeled.",
      "SLinks are powered by the same hybrid post-quantum cryptography used across the Majikah ecosystem — Ed25519 and ML-DSA-87. The signature is quantum-safe today and designed to remain valid for decades.",
    ],
    highlights: [
      {
        icon: "link",
        label: "Claim Any Public URL",
        description:
          "YouTube, X, Instagram, TikTok, GitHub, personal websites, and more. If it's publicly accessible, you can sign it.",
      },
      {
        icon: "shieldCheck",
        label: "Cryptographically Verified",
        description:
          "Ownership and Attribution claims are signed with your Majik Key and independently checkable by anyone — no need to contact you.",
      },
      {
        icon: "eye",
        label: "Public and Trustless",
        description:
          "Published SLinks appear on your MUID public profile. Anyone can view and verify them — no account, no software needed.",
      },
      {
        icon: "fingerprint",
        label: "Tied to Your Identity",
        description:
          "Each SLink is bound to your Majik Universal ID. If your MUID is verified, that trust carries through to your SLinks too.",
      },
      {
        icon: "hash",
        label: "Same URL, Same Code — Always",
        description:
          "Challenge codes are derived deterministically from the URL. The same resource always produces the same code, regardless of tracking parameters or URL variations.",
      },
      {
        icon: "trash",
        label: "Manage Anytime",
        description:
          "View, share, or remove any SLink from your My SLinks panel whenever you want.",
      },
    ],
    blocks: [
      {
        type: "badgeRow",
        title: "Three Claim Types — Pick What's True",
        columns: 3,
        badges: [
          {
            icon: "shieldCheck",
            label: "Ownership",
            description:
              "You control the domain itself. Proven with a DNS TXT record — the strongest proof, independent of any single page.",
          },
          {
            icon: "userCircle",
            label: "Attribution",
            description:
              "You control the content at this URL, but not the domain — a channel, profile, or repo. Proven by a code embedded on the page.",
          },
          {
            icon: "usersThree",
            label: "Reference",
            description:
              "You control neither, but want to sign a statement about the URL anyway. Recorded as a signed attestation — never shown as verified.",
          },
        ],
      },
    ],
  },
  features: [
    {
      id: "claim-types",
      title: "Choose the Claim That Matches Reality",
      description:
        "Majik SLink never lets you overstate a claim. The claim type you pick up front determines exactly how — or whether — your proof gets checked.",
      icon: "sealCheck",
      content: [
        "Ownership means you control the domain itself. It's proven with a DNS TXT record, which is the most authoritative method available — it lives in DNS, not on the page, so it survives redesigns.",
        "Attribution means you control the content at a URL but not the domain — a YouTube channel, an X profile, a GitHub account, a Patreon page. It's proven by embedding a short code somewhere visible on the page.",
        "Reference means you control neither the domain nor the content. There's no proof to check, so it's recorded as a signed attestation and always labeled that way — Majik Signature never shows a Reference claim as \"verified\".",
        "The claim type is selected before anything else, right alongside the URL, so every SLink is honest about what it actually proves from the moment it's created.",
      ],
    },
    {
      id: "challenge-response",
      title: "How Claims Are Proved",
      description:
        "Ownership and Attribution claims use a simple challenge-response model — no domain admin API access, no platform integrations, no special permissions beyond what you'd normally have.",
      icon: "checkCircle",
      content: [
        "When you enter a URL and pick Ownership or Attribution, a unique challenge code is generated for that specific resource — prefixed majik-slink: followed by a short hash of the URL.",
        "For Ownership, you publish that code as a DNS TXT record on your domain. For Attribution, you paste it somewhere visible on the page itself — a bio, description, or README.",
        "Once it's live, Majik Signature checks for it — a DNS lookup for Ownership, a page fetch for Attribution — and if found, your key signs the URL and the claim is finalized as Verified.",
        "Reference claims skip this entirely: there's nothing to publish, and clicking Sign records the attestation immediately.",
        "The code only needs to be live during that one check. You can safely remove it afterward — the signed record itself is permanent.",
      ],
      table: {
        headers: ["Platform", "Where the Attribution code goes"],
        rows: [
          {
            cells: ["YouTube", "Video description or channel About section"],
          },
          { cells: ["X / Twitter", "Profile bio or a pinned post"] },
          { cells: ["Instagram", "Profile bio"] },
          {
            cells: [
              "Everything else — GitHub, TikTok, personal sites, blogs, etc.",
              "Anywhere visible in the page's content",
            ],
          },
        ],
      },
    },
    {
      id: "deterministic-code",
      title: "One URL, One Code — Always the Same",
      description:
        "The challenge code is derived from the URL itself, not from a random value. The same page always produces the same code — paste it once and it's valid for that verification attempt whenever you're ready.",
      icon: "hash",
      content: [
        "Query parameters and tracking tokens are stripped before the code is generated. A YouTube link with and without ?v= produces identical results.",
        "Because the code is deterministic, you can preview it before signing anything — see exactly what you'd need to paste, ahead of committing to the claim.",
        "The code format is majik-slink: followed by the first 32 characters of the SHA-256 hash of the normalized URL — short enough to paste into any bio or description field.",
      ],
    },
    {
      id: "public-verification",
      title: "Anyone Can Verify — No Account Required",
      description:
        "Once published, a SLink has a permanent public page anyone can visit to check the claim — no login, no software, no technical knowledge needed.",
      icon: "eye",
      content: [
        "Every published SLink gets a public URL in the format slink.majikah.solutions/l/{id}.",
        "The page shows the signed URL, the claim type and its proof strength, who signed it, and the full cryptographic proof details.",
        "Visitors can open the original signed URL directly to confirm it matches what was claimed.",
        "The Cryptographic Proof section shows the raw Ed25519 and ML-DSA-87 public keys used to produce the signature, for anyone who wants to verify independently.",
        "All published SLinks also appear on the owner's public MUID profile.",
      ],
      blocks: [
        {
          type: "statusCard",
          status: "valid",
          title: "SLink Verified",
          subtitle: "youtube.com/@example — Ownership claim",
          checks: [
            "Challenge code found in DNS TXT record",
            "Signature valid (Ed25519 + ML-DSA-87)",
            "Linked to a Majik Universal ID",
          ],
          footerLabel: "Published to the Majikah registry",
        },
      ],
    },
    {
      id: "search",
      title: "Check Any URL in the Registry",
      description:
        "Not sure if a URL has been signed? Check it directly at slink.majikah.solutions, or from Verify mode inside Majik Signature — no account required either way.",
      icon: "magnifyingGlass",
      content: [
        "Enter any public URL and the registry is checked for every claim that's ever been signed for it — Ownership, Attribution, and Reference alike.",
        "Results show a count of claims by type, when the first one was signed, and a card for each signer with their claim type, proof strength, and status.",
        "Expand any card for the full cryptographic details — hash, challenge code, signer's Majik Universal ID, and key fingerprint — each copyable with one click.",
        "No match found simply means the URL hasn't been claimed yet — you're free to be the first, if you own or control it.",
      ],
    },
    {
      id: "identity-binding",
      title: "Tied to Your Verified Identity",
      description:
        "Every SLink is permanently linked to your Majik Universal ID. If your MUID is verified, that verification carries through to all your SLinks.",
      icon: "fingerprint",
      content: [
        "Your MUID doesn't need to be verified to publish SLinks — but a verified MUID adds a trust signal to your SLink pages.",
        "Anyone viewing your SLink can jump to your MUID public profile to see your full verification status and everything else you've signed.",
        "SLinks from a Trusted-tier MUID (all four Didit stages passed) provide the strongest assurance — the claim is backed by a KYC-verified real-world identity, on top of the cryptographic proof.",
        "If you delete your MUID, all your SLinks are removed as well.",
      ],
    },
    {
      id: "management",
      title: "Manage All Your SLinks in One Place",
      description:
        "View, share, and remove all your published SLinks from the My SLinks panel, right inside the Sign a Link view.",
      icon: "list",
      content: [
        "Open My SLinks to see every claim you've published, each row showing its source platform, claim type, status, and how long ago it was signed.",
        "Copy the public share link for any SLink with a single click, or open its verification page directly.",
        "Remove any SLink anytime — this takes it out of the registry and off your public profile immediately.",
        "SLinks can't be edited after publishing. To update a claim, remove the existing one and publish a new SLink for the same URL.",
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "How URLs Are Normalized",
        description:
          "The URL is stripped of query parameters, fragments, and trailing slashes before signing. This canonical form is what gets hashed and signed.",
        table: {
          headers: ["Component", "Example", "Notes"],
          rows: [
            {
              cells: [
                "Protocol + version prefix",
                "majik-slink-v1:",
                "Domain separator — prevents cross-protocol reuse",
              ],
            },
            {
              cells: [
                "Subdomain",
                "www",
                "Preserved as-is; empty string if absent",
              ],
            },
            {
              cells: [
                "Domain (SLD)",
                "youtube",
                "Second-level domain extracted from the registered domain",
              ],
            },
            { cells: ["TLD", "com", "Top-level domain"] },
            {
              cells: [
                "Path",
                "/watch/dQw4w9WgXcQ",
                "No query params, no fragment, no trailing slash",
              ],
            },
          ],
        },
        blocks: [
          {
            type: "codeOrEnvelope",
            language: "text",
            title: "Canonical string → challenge code",
            content:
              'canonical = "majik-slink-v1:www::youtube::com::/watch/dQw4w9WgXcQ"\nhash      = SHA256(canonical)\nv_code    = "majik-slink:" + hash.slice(0, 32)\n\n// Result:\n// majik-slink:a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6',
          },
        ],
      },
      {
        title: "Claim Types",
        description:
          "The claim type is fixed at creation and determines the proof requirement for that SLink.",
        table: {
          headers: [
            "Claim type",
            "Proven by",
            "Independently checkable?",
            "Displayed as",
          ],
          rows: [
            {
              cells: [
                "Ownership",
                "DNS TXT record",
                "Yes",
                "Verified (strong proof)",
              ],
            },
            {
              cells: [
                "Attribution",
                "Code embedded on the page",
                "Yes",
                "Verified (medium proof)",
              ],
            },
            {
              cells: [
                "Reference",
                "Nothing — signed attestation only",
                "No",
                'Attested by <signer> — never "verified"',
              ],
            },
          ],
        },
      },
      {
        title: "Verification Status Values",
        description:
          "Every SLink has a status reflecting the outcome of its check — DNS lookup for Ownership, page scrape for Attribution.",
        table: {
          headers: ["Status", "Meaning"],
          rows: [
            {
              cells: [
                "unverified",
                "Created, but a check hasn't been attempted yet",
              ],
            },
            { cells: ["pending", "A DNS lookup or page check is in progress"] },
            {
              cells: [
                "verified",
                "The challenge code was found and the signature is valid",
              ],
            },
            {
              cells: [
                "failed",
                "A check ran, but the challenge code wasn't found",
              ],
            },
            {
              cells: [
                "sig_invalid",
                "The challenge code was found, but the cryptographic signature did not verify",
              ],
            },
          ],
        },
        blocks: [
          {
            type: "paragraph",
            text: "Reference claims don't go through a DNS or page check, so these statuses describe Ownership and Attribution claims. A Reference claim is always presented as an attestation regardless of its underlying status, since no independent check applies to it.",
          },
        ],
      },
      {
        title: "Supported Platforms",
        description:
          "SLinks work for any https:// URL. The source platform is auto-detected from the domain for display purposes only — it does not change which claim types or proof methods are available.",
        table: {
          headers: ["Platform", "Auto-Detected?", "Example URL"],
          rows: [
            {
              cells: ["YouTube", "Yes", "https://youtube.com/channel/UCxxxxx"],
            },
            { cells: ["X / Twitter", "Yes", "https://x.com/username"] },
            { cells: ["Instagram", "Yes", "https://instagram.com/username"] },
            { cells: ["TikTok", "Yes", "https://tiktok.com/@username"] },
            { cells: ["GitHub", "Yes", "https://github.com/username"] },
            {
              cells: [
                "Personal website / blog",
                "Generic",
                "https://yourdomain.com",
              ],
            },
            {
              cells: [
                "Any other public URL",
                "Generic",
                "https://any-public-page.com/path",
              ],
            },
          ],
        },
      },
      {
        title: "Platform Support",
        description:
          "Majik SLink works in the browser, inside Majik Signature, and as an npm package for developers.",
        table: {
          headers: ["Platform", "Status", "Notes"],
          rows: [
            {
              cells: [
                "Majik Signature — Sign a Link panel",
                "✓ Available",
                "Create, verify, and manage SLinks from inside the app",
              ],
            },
            {
              cells: [
                "SLink Web App",
                "✓ Available",
                "id.majikah.solutions/slink — create and manage SLinks",
              ],
            },
            {
              cells: [
                "SLink Search Page",
                "✓ Available",
                "slink.majikah.solutions — search or verify any URL, no login",
              ],
            },
            {
              cells: [
                "Modern browsers",
                "✓ Supported",
                "Chrome, Firefox, Safari, Edge",
              ],
            },
            {
              cells: [
                "Node.js 18+",
                "✓ Supported",
                "Available as @majikah/majik-slink on npm",
              ],
            },
            {
              cells: [
                "Deno / Bun",
                "✓ Supported",
                "No native bindings required",
              ],
            },
          ],
        },
      },
    ],
  },
  howItWorks: {
    title: "How Majik SLink Works",
    processes: [
      {
        title: "Creating and Publishing a SLink",
        description:
          "The full process from entering a URL to having a live, publicly verifiable claim takes about 3–7 minutes — less for a Reference claim, since there's no proof step.",
        useStepTracker: true,
        steps: [
          {
            text: "Enter the URL you want to sign. Query parameters are stripped automatically — the same resource always produces the same challenge code.",
            type: "numbered",
          },
          {
            text: "Choose a claim type: Ownership if you control the domain, Attribution if you control the content but not the domain, or Reference if you control neither and just want to sign an attestation.",
            type: "numbered",
          },
          {
            text: "Prove it. For Ownership, add the generated code as a DNS TXT record on your domain. For Attribution, paste it somewhere visible on the page. Reference claims skip this step entirely.",
            type: "numbered",
          },
          {
            text: "Confirm. Majik Signature checks the DNS record or page (skipped for Reference), then signs the canonical URL with your Majik Key.",
            type: "numbered",
          },
          {
            text: "Review the result — a breakdown of the signed URL's subdomain, domain, TLD, and path for Ownership/Attribution, or immediate confirmation for a Reference attestation.",
            type: "numbered",
          },
          {
            text: "Share. Your SLink is now live with a permanent public verification link you can put anywhere.",
            type: "numbered",
          },
        ],
        note: "After publishing, you can safely remove the challenge code from your DNS record or page — it's no longer needed once the claim is finalized.",
      },
      {
        title: "Verifying a SLink",
        description:
          "There are two ways to check a claim — open a specific SLink page directly, or search for any URL.",
        steps: [
          {
            text: "Option A — Open the SLink page directly. Visit slink.majikah.solutions/l/{id}. The page shows the signed URL, the claim type and proof strength, who signed it, and the full cryptographic proof.",
            type: "bullet",
          },
          {
            text: "Option B — Search by URL. Go to slink.majikah.solutions, or use Verify mode inside Majik Signature, and enter any URL. If claims exist, you'll see results broken down by claim type with a card for each signer. If none exist, you're invited to claim it yourself.",
            type: "bullet",
          },
          {
            text: "Open the original signed URL from the SLink page to confirm it matches what was claimed.",
            type: "numbered",
          },
          {
            text: "Open the signer's Majik Universal ID profile from their claim card to check their verification tier and see everything else they've signed.",
            type: "numbered",
          },
        ],
        note: "Verification is completely public — no account, no login, no software required.",
      },
      {
        title: "Managing Your SLinks",
        description:
          "All your published SLinks are accessible from the My SLinks panel.",
        steps: [
          {
            text: "Open the Sign a Link panel and click My SLinks. A panel opens showing every SLink you've published.",
            type: "numbered",
          },
          {
            text: "Hover any row to reveal its actions: copy the share link, open the public verification page, or open the original URL.",
            type: "numbered",
          },
          {
            text: "Where a remove option is available, use it to take a SLink out of the registry and your public profile immediately.",
            type: "numbered",
          },
          {
            text: "To re-publish an updated or removed URL, go through the creation flow again from step one.",
            type: "numbered",
          },
        ],
        note: "SLinks can't be edited after publishing — the signed record is immutable. Remove and republish if anything needs to change.",
      },
    ],
  },
  pricing: {
    title: "Pricing",
    model: "freemium",
    currentStatus:
      "Majik SLink is completely **free**. Creating and publishing SLinks of any claim type, verifying them, and managing your published records all cost nothing.",
    futureStatus:
      "**Coming soon:** Premium features such as higher re-verification frequency and team-level management may be introduced in the future. Core SLink creation and verification will always remain free.",
    tiers: [
      {
        id: "free",
        name: "Free",
        price: "$0",
        priceSuffix: "forever",
        description:
          "Everything you need to create, verify, and manage SLinks of any claim type.",
        featured: true,
        ctaLabel: "Sign a URL",
        ctaUrl: "https://id.majikah.solutions/slink",
      },
    ],
    disclaimer:
      "Pricing for any future premium tier has not been announced. Core SLink functionality will remain free.",
  },
  security: {
    title: "Security — What You Need to Know",
    categories: [
      {
        title: "What a SLink Proves",
        items: [
          {
            label: "Domain or content control at verification time:",
            description:
              "A verified Ownership or Attribution SLink proves that the holder of a specific Majik Key controlled the domain's DNS or the page's content when the check was run.",
          },
          {
            label: "Cryptographic authenticity:",
            description:
              "The signature covers the canonical URL — any attempt to substitute a different URL while keeping the same signature will fail verification.",
          },
          {
            label: "Identity link:",
            description:
              "Every SLink is tied to a specific MUID. If that MUID is Verified or Trusted tier, the claim is also backed by a KYC-checked real-world identity.",
          },
          {
            label: "Quantum-safe signatures:",
            description:
              "Both Ed25519 and ML-DSA-87 must be broken simultaneously to forge a SLink. The proof is designed to remain valid for decades.",
          },
        ],
      },
      {
        title: "What a SLink Does NOT Prove",
        items: [
          {
            label: "A Reference claim proves nothing independently:",
            description:
              "It's a signed statement, not a checked proof. Treat it as an opinion or attestation from the signer, never as confirmed ownership or control.",
          },
          {
            label: "Ongoing control:",
            description:
              "Ownership and Attribution claims prove control at the moment the check was run — not continuously. Someone could lose control of a domain or page after publishing a SLink.",
          },
          {
            label: "Page content authenticity:",
            description:
              "A SLink proves control of a URL, not that the content currently on that page is accurate or unchanged.",
          },
          {
            label: "Real-world identity (without KYC):",
            description:
              "A SLink from an unverified MUID proves a key controls a URL, but not who holds that key. For real-world identity assurance, check the signer's MUID verification tier.",
          },
        ],
      },
      {
        title: "Good Practices",
        items: [
          {
            label: "Check the claim type first:",
            description:
              "Ownership and Attribution are independently checkable; Reference is not. Weight your trust accordingly before treating any claim as confirmed.",
          },
          {
            label: "Check the signer's MUID tier:",
            description:
              "A Trusted or Verified MUID adds real-world identity assurance behind the claim, on top of the cryptographic proof.",
          },
          {
            label: "Confirm the status before trusting:",
            description:
              'Open the SLink page and confirm the status reads "verified" — not "unverified", "pending", or "failed".',
          },
          {
            label: "For domain ownership, DNS is authoritative:",
            description:
              "An Ownership claim's DNS TXT record is the strongest available proof — it doesn't depend on page content staying unchanged.",
          },
        ],
      },
    ],
    badges: [
      {
        icon: "shieldCheck",
        label: "Ed25519 + ML-DSA-87",
        description: "Hybrid post-quantum signatures on every claim.",
      },
      {
        icon: "eye",
        label: "Publicly verifiable",
        description: "No account or software required to check a claim.",
      },
      {
        icon: "fingerprint",
        label: "Identity-linked",
        description: "Every SLink is tied to a Majik Universal ID.",
      },
    ],
  },
  useCases: [
    {
      title: "Prove You Own Your Social Profiles",
      description:
        "Sign your YouTube channel, X account, Instagram bio, or TikTok profile as an Attribution claim, and anyone can verify the connection — trustlessly, without relying on platform-issued verification badges.",
      icon: "userCircle",
    },
    {
      title: "Claim Your Developer Presence",
      description:
        "Link your Majikah identity to your GitHub profile, npm packages, or personal developer site. Clients, employers, and collaborators can verify the connection without contacting you.",
      icon: "githubLogo",
    },
    {
      title: "Protect Your Domain and Website",
      description:
        "Prove Ownership of a domain or website with a DNS TXT record — the strongest available proof, unaffected by page redesigns. Gives visitors a cryptographic way to confirm your site is the real one.",
      icon: "globe",
    },
    {
      title: "Secure Sensitive Announcements",
      description:
        "Published a statement, report, or announcement somewhere online? Sign the URL as Ownership or Attribution so anyone can verify it came from you and hasn't been swapped or redirected since.",
      icon: "megaphone",
    },
    {
      title: "Creator and Artist Attribution",
      description:
        "Sign your platform pages — Bandcamp, Behance, Patreon, personal portfolio — as Attribution claims to link your Majikah identity to your creative presence. Buyers and fans can verify provenance with one click.",
      icon: "paintBrush",
    },
    {
      title: "Cite a Source Without Overclaiming",
      description:
        "Need to sign your name to a statement about a URL you don't own or control — a citation, a review, a piece of commentary? A Reference claim records your attestation honestly, without implying ownership you don't have.",
      icon: "link",
    },
    {
      title: "KYC-Backed URL Ownership",
      description:
        "When your MUID is Verified or Trusted tier, your Ownership and Attribution SLinks carry real-world identity assurance. Useful for regulated platforms, professional credentials, or any context where identity-backed ownership matters.",
      icon: "sealCheck",
    },
    {
      title: "API and Webhook Endpoint Ownership",
      description:
        "Prove that a specific API endpoint or public-facing service URL is operated by your identity, via a DNS TXT Ownership claim. Useful for developers building on the Majikah ecosystem or integrating with third-party services.",
      icon: "code",
    },
  ],
  gettingStarted: {
    title: "Getting Started",
    steps: [
      {
        title: "1. You Need a Majik Universal ID",
        items: [
          "SLinks are tied to a Majik Universal ID. If you don't have one yet, you can create it right inside the Sign a Link panel, or ahead of time at id.majikah.solutions/id.",
          "Your MUID doesn't need to be verified — but a verified MUID adds a trust signal to your SLink pages.",
          "See Getting Started with Majik Universal ID for the full setup guide.",
        ],
      },
      {
        title: "2. Enter the URL and Choose a Claim Type",
        items: [
          "Open the Sign a Link panel — inside Majik Signature, or at id.majikah.solutions/slink.",
          "Type or paste the full URL of the page you're claiming.",
          "Pick Ownership (you control the domain), Attribution (you control the content, not the domain), or Reference (neither — a plain attestation).",
        ],
      },
      {
        title: "3. Prove Your Claim (Ownership / Attribution)",
        items: [
          "A challenge code is generated for your URL — something like majik-slink:a1b2c3d4…",
          "For Ownership, publish it as a DNS TXT record on your domain. For Attribution, paste it somewhere visible on the page — bio, description, or README.",
          "Reference claims skip this step entirely — there's nothing to publish.",
        ],
      },
      {
        title: "4. Confirm and Publish",
        items: [
          "Click Create & Continue (or Sign & Record Attestation for a Reference claim).",
          "For Ownership/Attribution, review the URL breakdown once the challenge is confirmed.",
          "Your SLink is now live, with a permanent public verification link you can share.",
          "You can remove the challenge code from your DNS or page now — it's no longer needed.",
        ],
      },
      {
        title: "5. Share Your SLink",
        items: [
          "Your SLink has a public verification URL: slink.majikah.solutions/l/{id}.",
          "Share it anywhere — social bios, email, documents, or embedded on your page.",
          "Anyone who opens it can see your claim type, proof strength, and your identity details.",
        ],
      },
      {
        title: "6. Manage Your SLinks",
        items: [
          "Click My SLinks from the Sign a Link panel to view all your published claims.",
          "Copy share links, open verification pages, visit original URLs, or remove SLinks from here.",
        ],
      },
    ],
  },
  roadmap: {
    title: "What's Coming Next",
    description:
      "Majik SLink is actively developed. Here is what's on the roadmap:",
    items: [
      {
        label: "Automated re-verification",
        description:
          "The system periodically re-checks DNS records and pages to confirm Ownership and Attribution claims are still current — with status updates in your panel.",
        status: "coming-soon",
      },
      {
        label: "Well-known endpoint support",
        description:
          "Verify domain ownership by serving the challenge code from a /.well-known/majik-slink endpoint — no DNS or page editing required.",
        status: "planned",
      },
      {
        label: "QR code export",
        description:
          "Export your SLink verification URL as a QR code to embed on physical materials or in video content.",
        status: "planned",
      },
      {
        label: "Batch publishing",
        description:
          "Sign and publish multiple URLs at once from the My SLinks panel.",
        status: "planned",
      },
      {
        label: "Notification on status change",
        description:
          "Get notified when a SLink's verification status changes — useful for monitoring pages or domains that may change after publishing.",
        status: "planned",
      },
      {
        label: "Mobile app",
        description: "Create and share SLinks from iOS and Android.",
        status: "planned",
      },
    ],
  },
  ecosystem: {
    title: "Part of the Majikah Ecosystem",
    paragraphs: [
      "Majik SLink is built on top of Majik Universal ID and Majik Signature. Your MUID provides the identity anchor for every claim you publish. Majik Signature provides the hybrid post-quantum signing engine — every SLink carries a full Ed25519 + ML-DSA-87 signature that both must verify.",
      "All your published SLinks appear on your MUID public profile page, giving anyone a single place to see everything you've claimed and verify each one, claim type and all.",
      "Your Majik Key works across all Majikah products. The same key that signs your files in Majik Signature and secures your messages in Majik Message is the one that proves your SLink claims.",
      "Manage everything — your MUID, your SLinks, and your key — from one place at id.majikah.solutions.",
    ],
  },
  cta: {
    title: "Sign Your First Claim — Cryptographically",
    description:
      "Majik SLink is free. Start proving ownership, attribution, or reference in minutes:",
    downloads: [
      { platform: "Sign a URL", url: "https://id.majikah.solutions/slink" },
      { platform: "Verify a SLink", url: "https://slink.majikah.solutions" },
      {
        platform: "npm (@majikah/majik-slink)",
        url: "https://www.npmjs.com/package/@majikah/majik-slink",
      },
      { platform: "GitHub", url: "https://github.com/Majikah/majik-slink" },
    ],
    closingStatement:
      "Trustless proof of ownership, attribution, or reference — verifiable by anyone, powered by post-quantum cryptography.",
  },
};
